import {
  getExampleNumber as getLibExampleNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

export type { CountryCode };

/**
 * Phone handling, from `libphonenumber-js` rather than by hand.
 *
 * The onboarding form used to hardcode `+62 21-1234-5678` as its placeholder
 * regardless of country, and validated the field as a bare string with no
 * format rule at all. The placeholder is now a real example number for whatever
 * country the customer selected.
 *
 * Phone is optional at signup, so validation only runs on a non-empty value.
 */

/** A realistic example number for the country, used as the field placeholder. */
export const getExampleNumber = (countryCode?: CountryCode): string => {
  if (!countryCode) return '+1 555 000 0000';
  try {
    const example = getLibExampleNumber(countryCode, examples);
    return example ? example.formatInternational() : '+1 555 000 0000';
  } catch {
    return '+1 555 000 0000';
  }
};

/**
 * True when the value is blank (the field is optional) or is a valid number for
 * the given country.
 */
export const isValidPhoneNumber = (
  value: string | undefined,
  countryCode?: CountryCode
): boolean => {
  if (!value || value.trim().length === 0) return true;
  try {
    const parsed = parsePhoneNumberFromString(value.trim(), countryCode);
    return !!parsed?.isValid();
  } catch {
    return false;
  }
};

/** Normalises to E.164 for storage, falling back to the raw input. */
export const toE164 = (
  value: string | undefined,
  countryCode?: CountryCode
): string | undefined => {
  if (!value || value.trim().length === 0) return undefined;
  try {
    const parsed = parsePhoneNumberFromString(value.trim(), countryCode);
    return parsed?.isValid() ? parsed.number : value.trim();
  } catch {
    return value.trim();
  }
};
